import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Phone, MessageCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useContact } from '@/hooks/useContacts';
import { useDeals } from '@/hooks/useDeals';
import { useNotes } from '@/hooks/useNotes';

function contactName(contact: any) {
  const full = contact?.full_name?.trim();
  if (full) return full;
  const first = contact?.first_name || '';
  const last = contact?.last_name || '';
  return `${first} ${last}`.trim() || 'Contato';
}

function normalizePhone(phone?: string | null) {
  if (!phone) return '';
  return phone.replace(/[^\d+]/g, '');
}

function formatDate(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('pt-BR');
}

export default function MobileContactDetail() {
  const { id } = useParams();
  const { contact, loading, error } = useContact(id || '');
  const { deals: allDeals } = useDeals({ stageFilter: 'all' });

  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const {
    notes,
    loading: notesLoading,
    createNote,
  } = useNotes({ contactId: id || undefined });

  const deals = useMemo(() => {
    return (allDeals || []).filter((deal: any) => deal.contact_id === id).slice(0, 20);
  }, [allDeals, id]);

  const activities = useMemo(() => {
    const noteItems = (notes || []).map((note) => ({
      id: `note-${note.id}`,
      created_at: note.created_at,
      label: 'Nota',
      detail: note.content || '[sem texto]',
    }));

    const dealItems = deals.map((deal: any) => ({
      id: `deal-${deal.id}`,
      created_at: deal.created_at,
      label: 'Negocio',
      detail: `${deal.name || 'Negocio sem nome'} • ${deal.stage || 'Sem etapa'}`,
    }));

    return [...noteItems, ...dealItems].sort((a, b) => {
      return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
    });
  }, [notes, deals]);

  const phone = normalizePhone(contact?.primary_phone);
  const name = useMemo(() => contactName(contact), [contact]);

  const handleCreateNote = async () => {
    const content = newNote.trim();
    if (!content || !id) return;

    setSavingNote(true);
    try {
      await createNote({ content });
      setNewNote('');
    } finally {
      setSavingNote(false);
    }
  };

  if (loading) return <div className="p-3 text-sm text-muted-foreground">Carregando contato...</div>;
  if (error || !contact) return <div className="p-3 text-sm text-destructive">Contato nao encontrado.</div>;

  return (
    <div className="space-y-3 p-3">
      <div className="rounded-lg border bg-card p-3">
        <p className="text-base font-semibold">{name}</p>
        <p className="mt-1 text-sm text-muted-foreground">{contact.primary_email || 'Sem email'}</p>
        <p className="text-sm text-muted-foreground">{contact.primary_phone || 'Sem telefone'}</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <a
          href={phone ? `tel:${phone}` : '#'}
          className="flex items-center justify-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm"
        >
          <Phone className="h-4 w-4" /> Ligar
        </a>
        <Link
          to={`/m/whatsapp/${contact.id}`}
          className="flex items-center justify-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm"
        >
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </Link>
      </div>

      <Tabs defaultValue="info" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="info">Info</TabsTrigger>
          <TabsTrigger value="deals">Negocios</TabsTrigger>
          <TabsTrigger value="notes">Notas</TabsTrigger>
          <TabsTrigger value="activity">Atividade</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-3">
          <div className="rounded-lg border bg-card p-3 text-sm">
            <p><span className="font-medium">Cidade:</span> {contact.city || 'N/A'}</p>
            <p><span className="font-medium">Status:</span> {contact.status || 'N/A'}</p>
            <p><span className="font-medium">Criado em:</span> {formatDate(contact.created_at)}</p>
          </div>
        </TabsContent>

        <TabsContent value="deals" className="mt-3">
          <div className="space-y-2">
            {deals.length === 0 ? <p className="text-xs text-muted-foreground">Sem negocios cadastrados.</p> : null}
            {deals.map((deal: any) => (
              <Link key={deal.id} to={`/m/deals/${deal.id}`} className="block rounded-md border bg-card p-2">
                <p className="text-sm font-medium">{deal.name || 'Negocio sem nome'}</p>
                <p className="text-xs text-muted-foreground">{deal.stage || 'Sem etapa'}</p>
              </Link>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="notes" className="mt-3 space-y-2">
          <div className="rounded-lg border bg-card p-2">
            <textarea
              value={newNote}
              onChange={(event) => setNewNote(event.target.value)}
              placeholder="Adicionar nota..."
              className="min-h-[80px] w-full resize-none rounded-md border p-2 text-sm"
            />
            <Button className="mt-2 w-full" disabled={savingNote || !newNote.trim()} onClick={handleCreateNote}>
              {savingNote ? 'Salvando...' : 'Salvar nota'}
            </Button>
          </div>

          {notesLoading ? <p className="text-sm text-muted-foreground">Carregando notas...</p> : null}
          {(notes || []).map((note) => (
            <div key={note.id} className="rounded-lg border bg-card p-3">
              <p className="whitespace-pre-wrap text-sm">{note.content}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">{formatDate(note.created_at)}</p>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="activity" className="mt-3">
          <div className="space-y-2">
            {activities.length === 0 ? <p className="text-xs text-muted-foreground">Sem atividade recente.</p> : null}
            {activities.map((item) => (
              <div key={item.id} className="rounded-lg border bg-card p-3">
                <p className="text-sm font-medium">{item.label}</p>
                <p className="mt-1 text-xs text-muted-foreground">{item.detail}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">{formatDate(item.created_at)}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
